import React from 'react';
import { Box, Button, Text, Grid, VStack, useToast } from '@chakra-ui/react';
import {
  useArticleOpinionStats,
  useUserArticleOpinion,
  useSubmitArticleOpinion
} from '../../../../apis/queries/useArticleOpinion';
import ThumbsUp from '../../../../assets/thumbup-icon.svg';
import ThumbsDown from '../../../../assets/thumbdown-icon.svg';
import ThumbsUpDouble from '../../../../assets/thumbup-double-icon.svg';
import ThumbsDownDouble from '../../../../assets/thumbdown-double-icon.svg';
import DataLayer from 'src/utils/data-layer';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

const ArticleOpinion = ({ showSensitiveContentCard, articleId, isShowSensitive, article }) => {
  const toast = useToast();
  const { data: stats, isLoading: statsLoading } = useArticleOpinionStats(articleId);
  const { data: userOpinion, isLoading: opinionLoading } = useUserArticleOpinion(articleId);
  const { mutate: submitOpinion, isPending: isSubmitting } = useSubmitArticleOpinion();
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const opinions = [
    { id: 'OPPOSE', label: 'Oppose', Icon: ThumbsDownDouble },
    { id: 'SLIGHTLY_OPPOSE', label: 'Slightly Oppose', Icon: ThumbsDown },
    { id: 'SLIGHTLY_AGREE', label: 'Slightly Agree', Icon: ThumbsUp },
    { id: 'AGREE', label: 'Agree', Icon: ThumbsUpDouble }
  ];

  const handleOpinionSelect = (type) => {
    // Track article opinion event
    if (article) {
      const articleType = article.writingType?.name;
      DataLayer.trackArticleReact(articleType, articleId, type);
    }

    submitOpinion(
      { articleId, type },
      {
        onSuccess: () => {
          toast({
            title: 'Submitted',
            description: 'Thanks for sharing your take!',
            status: 'success',
            duration: 2000,
            isClosable: true
          });
        },
        onError: (error: any) => {
          if (error.status === 401) {
            if (!userId) return UnauthorizedDispatcher();
          }

          toast({
            title: 'Error submitting opinion',
            description: error.message || 'Please try again',
            status: 'error',
            duration: 3000,
            isClosable: true
          });
        }
      }
    );
  };

  if (statsLoading || opinionLoading) {
    return (
      <Box className="relative my-8">
        <Box className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 shadow-lg">
          <Text className="text-gray-400 text-center">Loading...</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className={`relative my-6 md:my-12 ${showSensitiveContentCard && !isShowSensitive && 'blur-lg'}`}
    >
      <Box className="p-6 rounded-xl border border-anti-flash-white shadow-[0px_4px_6px_-4px_rgba(0,0,0,0.1)]">
        <VStack spacing={4} align="stretch">
          <Text className="text-earth-brown-900 font-semibold text-lg text-center">
            What's your take on this article?
          </Text>

          <Grid templateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }} gap={3}>
            {opinions.map(({ id, label, Icon }) => {
              const count = stats?.[id] || 0;
              const isSelected = userOpinion?.type === id;

              return (
                <Button
                  bg="var(growth-green)"
                  height={'120px'}
                  key={id}
                  onClick={() => handleOpinionSelect(id)}
                  disabled={isSubmitting}
                  className={`
                    relative overflow-hidden transform transition-all duration-200 
                    flex flex-col items-center gap-2 p-1 rounded-lg
                    hover:scale-105 hover:shadow-md
                    ${isSelected ? 'bg-[#F0EDEC] border-0 hover:!bg-[#D1C8C4]' : 'bg-white hover:!bg-light-gray'}
                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      transform transition-transform duration-200
                      ${isSelected ? 'scale-110' : ''}
                    `}
                  >
                    <img src={Icon} className={`h-5 ${isSelected ? 'white' : 'black'}`} alt="" />
                  </span>
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    className={`
                      transition-colors duration-200
                      ${isSelected ? 'text-earth-brown-900' : 'text-jet-black'}
                    `}
                  >
                    {label}
                  </Text>
                  <Text
                    fontSize="xs"
                    className={`
                      transition-colors duration-200
                      ${isSelected ? 'text-earth-brown-900' : 'text-dim-gray'}
                    `}
                  >
                    {count} {count === 1 ? 'vote' : 'votes'}
                  </Text>
                </Button>
              );
            })}
          </Grid>

          {userOpinion && (
            <Text
              className="text-sm text-[#2D211C] text-center animate-fadeIn"
              sx={{
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'translateY(10px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' }
                },
                animation: 'fadeIn 0.3s ease-out'
              }}
            >
              Thanks for sharing your opinion!
            </Text>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default ArticleOpinion;
